"use client"

import { useEffect, useState, useCallback } from "react"
import { toast } from "sonner"
import {
  ListTodo,
  Plus,
  Check,
  Trash2,
  Phone,
  Clock,
  Bell,
  Calendar,
  X,
  Save,
} from "@/lib/icons"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { DateTimePicker } from "@/components/ui/date-time-picker"
import { supabase, createTask, completeTask, deleteTask, logLeadActivity } from "@/lib/supabase"

const TIPO_OPTIONS = [
  { value: "llamada", label: "Llamar", icon: Phone, color: "#8b5cf6", bg: "bg-violet-50 dark:bg-violet-950/25", text: "text-violet-700 dark:text-violet-300" },
  { value: "seguimiento", label: "Seguimiento", icon: Bell, color: "#0ea5e9", bg: "bg-sky-50 dark:bg-sky-950/25", text: "text-sky-700 dark:text-sky-300" },
  { value: "recordatorio", label: "Recordatorio", icon: Clock, color: "#f59e0b", bg: "bg-amber-50 dark:bg-amber-950/25", text: "text-amber-700 dark:text-amber-300" },
  { value: "cita", label: "Cita", icon: Calendar, color: "#10b981", bg: "bg-emerald-50 dark:bg-emerald-950/25", text: "text-emerald-700 dark:text-emerald-300" },
  { value: "manual", label: "Tarea", icon: ListTodo, color: "#64748b", bg: "bg-slate-100 dark:bg-slate-800", text: "text-slate-700 dark:text-slate-300" },
]

const PRIORIDAD_STYLES = {
  alta: "border-red-300 bg-red-50/60 dark:bg-red-950/20",
  normal: "border-slate-200 dark:border-slate-800",
  baja: "border-slate-100 dark:border-slate-900",
}

function formatDueDate(iso) {
  if (!iso) return null
  const d = new Date(iso)
  const now = new Date()
  const diffMs = d - now
  const diffH = diffMs / (1000 * 60 * 60)
  const diffD = Math.floor(diffH / 24)
  const hm = d.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })
  const dayLabel = d.toLocaleDateString("es-MX", { day: "numeric", month: "short" })
  const overdue = diffMs < 0
  const sameDay = d.toDateString() === now.toDateString()
  let label
  if (sameDay) label = `Hoy · ${hm}`
  else if (diffD === 1) label = `Mañana · ${hm}`
  else if (diffD === -1) label = `Ayer · ${hm}`
  else label = `${dayLabel} · ${hm}`
  return { label, overdue }
}

const EMPTY = { titulo: "", tipo: "llamada", fecha: "" }

export function LeadTasksSection({ leadId, asesorId = null, reloadKey = 0, onActivityChange, onStatsChange }) {
  const [tasks, setTasks] = useState([])
  const [loading, setLoading] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [draft, setDraft] = useState(EMPTY)
  const [saving, setSaving] = useState(false)
  const [showCompleted, setShowCompleted] = useState(false)

  const reload = useCallback(async () => {
    if (!leadId) return
    setLoading(true)
    const { data, error } = await supabase
      .from("tasks")
      .select("*")
      .eq("lead_id", leadId)
      .order("completada", { ascending: true })
      .order("fecha_vencimiento", { ascending: true, nullsFirst: false })
      .order("created_at", { ascending: false })
    if (error) {
      console.warn("[lead-tasks] reload error:", error.message)
      setTasks([])
    } else {
      setTasks(data || [])
    }
    setLoading(false)
  }, [leadId])

  useEffect(() => { reload() }, [reload, reloadKey])

  const pending = tasks.filter((t) => !t.completada)
  const completed = tasks.filter((t) => t.completada)

  useEffect(() => {
    if (!onStatsChange) return
    const now = Date.now()
    const overdue = pending.filter((t) => {
      if (!t.fecha_vencimiento) return false
      return new Date(t.fecha_vencimiento).getTime() < now
    }).length
    onStatsChange({ pending: pending.length, overdue, total: tasks.length })
  }, [tasks, onStatsChange])

  const handleAdd = async () => {
    if (!draft.titulo.trim()) {
      toast.error("El título es obligatorio")
      return
    }
    setSaving(true)
    const { error } = await createTask({
      leadId,
      asesorId,
      titulo: draft.titulo.trim(),
      tipo: draft.tipo,
      prioridad: "normal",
      fechaVencimiento: draft.fecha || null,
    })
    setSaving(false)
    if (error) {
      toast.error("No se pudo crear la tarea", { description: error.message })
      return
    }
    setDraft(EMPTY)
    setShowForm(false)
    toast.success("Tarea creada")
    reload()
  }

  const handleComplete = async (task) => {
    const { error } = await completeTask(task.id)
    if (error) {
      toast.error("No se pudo completar")
      return
    }
    toast.success("Tarea completada")
    logLeadActivity(leadId, {
      tipo: task.tipo === "llamada" ? "llamada" : "nota",
      titulo: `Tarea completada: ${task.titulo}`,
      descripcion: null,
      asesor_id: asesorId,
    }).then(() => onActivityChange?.())
    reload()
  }

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta tarea?")) return
    const { error } = await deleteTask(id)
    if (error) {
      toast.error("No se pudo eliminar")
      return
    }
    reload()
  }

  return (
    <div className="space-y-2.5">
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400 dark:text-slate-500 flex items-center gap-1.5">
          <ListTodo className="h-3 w-3" /> Tareas pendientes
          {pending.length > 0 && (
            <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[9px] font-bold bg-rose-600 text-white">
              {pending.length}
            </span>
          )}
        </p>
        <button
          type="button"
          onClick={() => setShowForm((v) => !v)}
          className="text-[10px] font-semibold text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/40 px-2 py-0.5 rounded-full hover:bg-rose-100 dark:hover:bg-rose-950/60 transition-colors flex items-center gap-1"
        >
          {showForm ? <X className="h-2.5 w-2.5" /> : <Plus className="h-2.5 w-2.5" />}
          {showForm ? "Cancelar" : "Nueva"}
        </button>
      </div>

      {showForm && (
        <div className="rounded-lg border border-rose-200 dark:border-rose-900/40 bg-rose-50/40 dark:bg-rose-950/20 p-2.5 space-y-2">
          <Input
            value={draft.titulo}
            onChange={(e) => setDraft((d) => ({ ...d, titulo: e.target.value }))}
            placeholder="Ej: Llamar para confirmar examen"
            className="h-8 text-xs"
            autoFocus
          />
          <div className="grid grid-cols-5 gap-1">
            {TIPO_OPTIONS.map((o) => {
              const Icon = o.icon
              const active = draft.tipo === o.value
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => setDraft((d) => ({ ...d, tipo: o.value }))}
                  className={`flex flex-col items-center gap-0.5 py-1.5 rounded-md border text-[9px] font-semibold transition-all ${
                    active
                      ? `${o.bg} ${o.text} border-current shadow-sm`
                      : "border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800"
                  }`}
                  title={o.label}
                >
                  <Icon className="h-3 w-3" />
                  {o.label}
                </button>
              )
            })}
          </div>
          <DateTimePicker
            value={draft.fecha}
            onChange={(iso) => setDraft((d) => ({ ...d, fecha: iso || "" }))}
            placeholder="Fecha límite (opcional)"
            variant="task"
          />
          <Button
            type="button"
            size="sm"
            className="w-full h-8 text-[11px] bg-rose-600 hover:bg-rose-700 text-white"
            disabled={saving || !draft.titulo.trim()}
            onClick={handleAdd}
          >
            <Save className="h-3 w-3 mr-1" />
            {saving ? "Creando..." : "Crear tarea"}
          </Button>
        </div>
      )}

      {loading && <p className="text-[11px] text-slate-400">Cargando…</p>}

      {!loading && pending.length === 0 && !showForm && (
        <p className="text-[11px] text-slate-400 italic">
          Sin tareas pendientes para este prospecto.
        </p>
      )}

      <div className="space-y-1.5">
        {pending.map((t) => {
          const tipoMeta = TIPO_OPTIONS.find((o) => o.value === t.tipo) || TIPO_OPTIONS[4]
          const Icon = tipoMeta.icon
          const due = formatDueDate(t.fecha_vencimiento)
          return (
            <div
              key={t.id}
              className={`group flex items-start gap-2 rounded-lg border bg-white dark:bg-slate-900/60 px-2.5 py-2 hover:border-rose-200 dark:hover:border-rose-900/50 transition-colors ${
                PRIORIDAD_STYLES[t.prioridad] || PRIORIDAD_STYLES.normal
              }`}
            >
              <button
                type="button"
                onClick={() => handleComplete(t)}
                className="flex-shrink-0 mt-0.5 h-4 w-4 rounded border-2 border-slate-300 dark:border-slate-600 hover:border-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors flex items-center justify-center group/check"
                title="Marcar completada"
              >
                <Check className="h-2.5 w-2.5 text-rose-600 opacity-0 group-hover/check:opacity-100" />
              </button>
              <div className="flex-1 min-w-0">
                <p className="text-[11.5px] font-semibold leading-tight break-words">
                  {t.titulo}
                </p>
                <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                  <span className={`inline-flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded ${tipoMeta.bg} ${tipoMeta.text}`}>
                    <Icon className="h-2 w-2" />
                    {tipoMeta.label}
                  </span>
                  {due && (
                    <span className={`inline-flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded ${
                      due.overdue
                        ? "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300"
                        : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"
                    }`}>
                      <Clock className="h-2 w-2" />
                      {due.label}
                    </span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleDelete(t.id)}
                className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-red-600 transition-all"
                title="Eliminar"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          )
        })}
      </div>

      {completed.length > 0 && (
        <div className="pt-1">
          <button
            type="button"
            onClick={() => setShowCompleted((v) => !v)}
            className="text-[10px] font-semibold text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
          >
            {showCompleted ? "Ocultar" : "Ver"} completadas ({completed.length})
          </button>
          {showCompleted && (
            <div className="mt-1.5 space-y-1">
              {completed.map((t) => (
                <div
                  key={t.id}
                  className="flex items-center gap-2 rounded-md px-2 py-1 bg-slate-50 dark:bg-slate-900/40 text-[10.5px]"
                >
                  <Check className="h-2.5 w-2.5 text-emerald-600 flex-shrink-0" />
                  <span className="flex-1 truncate text-slate-500 line-through">{t.titulo}</span>
                  <button
                    type="button"
                    onClick={() => handleDelete(t.id)}
                    className="text-slate-300 hover:text-red-500"
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
